<template>
  <f7-page name="agronomySamples">
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>

    <div v-if="loaded">
      <div class="custom-table-container tableContainer">
        <!-- Submenú en la parte superior del card -->
        <div>
          <SubMenu
            :items="MenuOptions.contexts.samples.itemsByArea.agronomy"
          />
        </div>

        <AgronomySamplesTable :store="customStore" />
      </div>
    </div>
  </f7-page>
</template>

<script>
import { mapActions } from 'vuex';
import EventBus from '@/services/event-bus';
import { NOTIFY_TIME_LONG, ATCS_ES } from '@/js/constants';
import MenuOptions from '@/json/menuOptions.json';
import SubMenu from '@/components/SubMenu/index.vue';
import AgronomySamplesTable from '../../components/Agronomy/AgronomySamplesTable/AgronomySamplesTable.vue';

export default {
  components: {
    AgronomySamplesTable,
    SubMenu,
  },
  data() {
    return {
      loaded: false,
      customStore: null,
      MenuOptions,
    };
  },

  beforeMount() {
    this.setCurrentRoute([
      {
        name: this.$t('Agronomy'),
        disabled: false,
        page: 'AgronomySamplesPage',
        active: false,
      },
      {
        name: this.$t('agronomySample.exportExcelSampleTable'),
        disabled: true,
        page: 'AgronomySamplesPage',
        active: true,
      },
    ]);

    this.setCurrentFilter('agronomy');

    // ✅ Preparar filtros globales si vienes desde dashboard (incluye Sin revisar)
    const flagAlert = this.$f7route?.query?.dashboardAlert === 'true';
    const flagInProcess =
      this.$f7route?.query?.dashboardInProcess === 'true';
    const flagNotReviewed =
      this.$f7route?.query?.dashboardNotReviewed === 'true';

    const fromDashboard =
      flagAlert || flagInProcess || flagNotReviewed;

    if (fromDashboard) {
      const params = {
        receptionDate_after: '',
        receptionDate_before: '',
        endDate_after: '',
        endDate_before: '',
        sample_type: '',
        analyse_type: '',
        atc_name: ATCS_ES.agronomy,
        summary_semaphore: '',
        status: '',
        not_seen: '',
      };

      if (flagAlert) params.summary_semaphore = 'red,orange';
      if (flagInProcess) params.status = '2';
      if (flagNotReviewed) params.not_seen = true;

      // ✅ últimos 6 meses
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);

      const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();
        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        return [year, month, day].join('-');
      };

      params.receptionDate_after = formatDate(startDate);
      params.receptionDate_before = formatDate(endDate);

      // Guardamos en el store global de filtros para que el DataSource se alinee
      this.setAllFilterParams(params);
    }
  },

  async mounted() {
    try {
      this.loaded = false;

      const isAlert = this.$f7route?.query?.dashboardAlert === 'true';
      const isInProcess =
        this.$f7route?.query?.dashboardInProcess === 'true';
      const isNotReviewed =
        this.$f7route?.query?.dashboardNotReviewed === 'true';

      const fromDashboard = isAlert || isInProcess || isNotReviewed;
      let hasQuickFilters = false;
      const raw = localStorage.getItem('agronomySamplesTable');
      if (raw) {
        try {
          const saved = JSON.parse(raw);
          const f = saved.isFilterActive || {};
          hasQuickFilters =
            f.inProcess ||
            f.finished ||
            f.cancelled ||
            f.lastMonth ||
            f.noReviewed ||
            f.onAlert ||
            f.noPositives;
        } catch (e) {
          console.warn(
            'agronomySamplesTable corrupto en localStorage',
            e
          );
        }
      }

      if (fromDashboard) {
        const params = {
          receptionDate_after: '',
          receptionDate_before: '',
          endDate_after: '',
          endDate_before: '',
          sample_type: '',
          analyse_type: '',
          atc_name: ATCS_ES.agronomy,
          summary_semaphore: '',
          status: '',
          not_seen: '',
        };

        if (isAlert) params.summary_semaphore = 'red,orange';
        if (isInProcess) params.status = '2';
        if (isNotReviewed) params.not_seen = true;

        const { receptionDate_after, receptionDate_before } =
          this.getLastMonthsDates(6);
        params.receptionDate_after = receptionDate_after;
        params.receptionDate_before = receptionDate_before;

        this.setAllFilterParams(params);
        this.customStore = await this.getStoreWithFilters(params);
      } else if (!hasQuickFilters) {
        setTimeout(() => {
          this.applyCurrentFilters();
        }, 0);
      }

      EventBus.$on('AgronomySamplesPage', async (params) => {
        if (params)
          this.customStore = await this.getStoreWithFilters(params);
        else this.customStore = await this.getStore();
      });
    } catch (error) {
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(
              JSON.parse(error.message).error
            )
          ),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    } finally {
      this.loaded = true;
    }
  },

  beforeDestroy() {
    EventBus.$off('AgronomySamplesPage');
  },

  methods: {
    getLastMonthsDates(months) {
      const endDate = new Date();
      const startDate = new Date(endDate);

      const originalDay = startDate.getDate();
      const targetMonth = startDate.getMonth() - months;

      startDate.setMonth(targetMonth);

      if (startDate.getDate() !== originalDay) {
        startDate.setDate(0);
      }

      const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();
        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        return [year, month, day].join('-');
      };

      return {
        receptionDate_after: formatDate(startDate),
        receptionDate_before: formatDate(endDate),
      };
    },

    ...mapActions('agronomySamples', [
      'getStore',
      'getStoreWithFilters',
    ]),
    ...mapActions('samples', ['setAllFilterParams']),
    ...mapActions('filter', [
      'applyCurrentFilters',
      'setCurrentFilter',
    ]),
    ...mapActions('breadcrumb', ['setCurrentRoute']),
  },
};
</script>
